import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { Button } from 'blaze-ui/Button'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import Styles from '../QuestionnairePanel.module.scss'
import cx from 'classnames'
import { Images } from 'blaze-ui/icons/30'
import { Text } from 'blaze-ui/Text/Text'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { Duration } from 'screens/Calendar/WeekViewV2/CalendarEventCard/Duration'
import type { AlmanacFile } from 'types'

/** Photos uploaded during this questionnaire session (persisted in parent while the panel is mounted). */
export interface QuestionnaireBrandPhoto {
  id: string
  thumbnail_url?: string | null
  source_url?: string | null
  duration?: number
}

interface Props {
  localPhotos: QuestionnaireBrandPhoto[]
  onPhotosUploaded: (files: AlmanacFile[]) => void
  onAdvance: () => void
  onSkip: () => void
  isOnboarding?: boolean
}

export const BrandPhotosStep = ({ localPhotos, onPhotosUploaded, onAdvance, onSkip, isOnboarding }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const imageLibrary = useCurrentImageLibrary()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const { data: brandProfile } = useBrandProfile()

  const uploadsProjectId = uploadsProjectQuery.data?.project?.id
  const brandKitId = brandProfile?.brand_kit?.id
  const projectKey = brandKitId && imageLibrary?.key ? imageLibrary.key : uploadsProjectId

  const { uploadImageFiles, isUploading } = useImportMediaFiles(false, (files) => {
    onPhotosUploaded(files)
    if (brandKitId) {
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
    }
    if (imageLibrary?.key) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    }
  })

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!projectKey) return
      uploadImageFiles(acceptedFiles, projectKey, brandKitId ? { brand_kit_id: brandKitId } : undefined)
    },
    [projectKey, brandKitId, uploadImageFiles],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
    noClick: localPhotos.length > 0,
    noDrag: localPhotos.length > 0,
  })

  if (localPhotos.length > 0) {
    if (isOnboarding && isMobile) {
      return (
        <>
          <div className={cx(Styles.imageGrid, Styles.onboardingImageGrid)}>
            {localPhotos.map((mediaFile) => (
              <div key={mediaFile.id} className={Styles.imageThumbContainer}>
                <img src={mediaFile.thumbnail_url || mediaFile.source_url || ''} alt="" className={Styles.imageThumb} />
                {!!mediaFile.duration && <Duration duration={mediaFile.duration} />}
              </div>
            ))}
          </div>
          <div className={Styles.verticalActions}>
            <FileTrigger
              onSelect={(fileList) => {
                if (!fileList || !projectKey) return
                uploadImageFiles(
                  Array.from(fileList),
                  projectKey,
                  brandKitId ? { brand_kit_id: brandKitId } : undefined,
                )
              }}
              accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
              allowsMultiple
            >
              <Button isDisabled={isUploading} size="lg" fullWidth>
                {isUploading ? (
                  <>
                    Uploading
                    <LoadingEllipses />
                  </>
                ) : (
                  'Upload more'
                )}
              </Button>
            </FileTrigger>
            <Button variant="secondary" onPress={onAdvance} size="lg" isDisabled={isUploading} fullWidth>
              Looks good!
            </Button>
          </div>
        </>
      )
    }

    return (
      <>
        <div className={Styles.imageGrid}>
          {localPhotos.map((mediaFile) => (
            <div key={mediaFile.id} className={Styles.imageThumbContainer}>
              <img src={mediaFile.thumbnail_url || mediaFile.source_url || ''} alt="" className={Styles.imageThumb} />
              {!!mediaFile.duration && <Duration duration={mediaFile.duration} />}
            </div>
          ))}
        </div>
        <div className={Styles.actions}>
          <FileTrigger
            onSelect={(fileList) => {
              if (!fileList || !projectKey) return
              uploadImageFiles(Array.from(fileList), projectKey, brandKitId ? { brand_kit_id: brandKitId } : undefined)
            }}
            accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
            allowsMultiple
          >
            <Button isDisabled={isUploading} size="lg">
              {isUploading ? 'Uploading...' : 'Upload more'}
            </Button>
          </FileTrigger>
          <Button onPress={onAdvance} size="lg" isDisabled={isUploading} variant="secondary">
            Looks good!
          </Button>
        </div>
      </>
    )
  }

  if (isOnboarding && isMobile) {
    return (
      <>
        <div {...getRootProps()} className={Styles.onboardingDropzone}>
          <input {...getInputProps()} />
          <Images size={38} color="var(--dark-40)" />
          <div className={Styles.onboardingDropzoneText}>
            <Text variant="secondary" color="var(--dark-60)">
              The more Blaze has to work with, the better your content gets.
            </Text>
          </div>
        </div>
        <div className={Styles.verticalActions}>
          <FileTrigger
            onSelect={(fileList) => {
              if (!fileList || !projectKey) return
              uploadImageFiles(Array.from(fileList), projectKey, brandKitId ? { brand_kit_id: brandKitId } : undefined)
            }}
            accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
            allowsMultiple
          >
            <Button isDisabled={isUploading} size="lg" fullWidth>
              {isUploading ? (
                <>
                  Uploading
                  <LoadingEllipses />
                </>
              ) : (
                'Upload photos'
              )}
            </Button>
          </FileTrigger>
          <Button
            variant="tertiary"
            onPress={onSkip}
            size="sm"
            color="var(--dark-60)"
            isDisabled={isUploading}
            fullWidth
          >
            Skip Step
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <div {...getRootProps()} className={`${Styles.dropzone} ${isDragActive ? Styles.dropzoneDragActive : ''}`}>
        <input {...getInputProps()} />
        <span className={Styles.dropzoneText}>
          {isDragActive ? (
            'Drop files here'
          ) : (
            <>
              Drag files here or <span className={Styles.dropzoneBrowse}>browse</span>
            </>
          )}
        </span>
        <span className={Styles.dropzoneHint}>Isolated images, benefits, description ...</span>
      </div>
      <div className={Styles.actions}>
        <Button variant="secondary" onPress={onSkip} size="lg">
          Skip
        </Button>
      </div>
    </>
  )
}
